/**
 * RouletteConfigForm — configuração de UMA roleta (controlado).
 *
 * UI/UX única compartilhada pelas duas telas de roleta do produto:
 *  - aba Etiquetas: um destes por grupo (até 5 grupos);
 *  - tool nativa de transferência (treino do agente): um só.
 * Mantém a identidade visual da aba Etiquetas (caixas âmbar) e traz os recursos
 * da roleta do agente: modo (ponderada/fixo), peso por membro com % e barra,
 * "adicionar todos de uma categoria" e guard (dono/admin/membro-com-autopilot
 * ficam de fora — senão o contato volta para a IA/dono).
 *
 * value: { mode:'round_robin'|'fixed', weights:{id:int}, fixed_member_id,
 *          target_category_id? }. onChange(next) recebe o value atualizado.
 */
import React, { useMemo } from 'react';
import { User, Trash2, Plus } from 'lucide-react';

const AMBER = '#f59e0b';
export const clampWeight = (w) => Math.max(1, Math.min(parseInt(w, 10) || 1, 100));
const isActive = (m) => (m?.account_status || 'active') === 'active';
const nameOf = (m) => m?.name || m?.email || m?.id;

const RouletteConfigForm = ({
  value,
  onChange,
  members = [],
  categories = [],
  memberAgents = {},
  company = null,
  colors,
  radioName = 'roulette',   // isola o grupo de radios do modo fixo (Etiquetas tem vários)
  allowFixed = true,        // false: só roleta ponderada (o assistente de saída tem "membro" como passo próprio)
}) => {
  const mode = (allowFixed && value?.mode === 'fixed') ? 'fixed' : 'round_robin';
  const weights = value?.weights || {};

  const memberById = useMemo(() => {
    const map = {};
    for (const m of members) map[m.id] = m;
    return map;
  }, [members]);

  // Guard: dono, admin ou membro com agente em autopilot não entram no pool.
  const guardReason = (m) => {
    if (!m) return null;
    if (company && m.id === company.owner_user_id) return 'Dono da empresa não entra na roleta.';
    if ((m.company_role || '') === 'admin') return 'Administradores não entram na roleta.';
    const am = memberAgents[m.id];
    if (am && am.autopilot_mode && am.autopilot_mode !== 'off') {
      return 'Membro com agente em autopilot — adicioná-lo enviaria os contatos de volta para a IA.';
    }
    return null;
  };

  const poolMembers = useMemo(() => (
    Object.keys(weights)
      .map((id) => memberById[id])
      .filter((m) => m && isActive(m))
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
  ), [weights, memberById]);

  const sumWeights = useMemo(
    () => poolMembers.reduce((s, m) => s + clampWeight(weights[m.id]), 0),
    [poolMembers, weights],
  );

  const addableMembers = useMemo(() => (
    members
      .filter((m) => isActive(m) && !(m.id in weights))
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
  ), [members, weights]);

  const pct = (id) => {
    if (mode === 'fixed') return value?.fixed_member_id === id ? 100 : 0;
    return sumWeights ? (clampWeight(weights[id]) / sumWeights) * 100 : 0;
  };

  // ---- Mutações (controlado) ----
  const mutate = (fn) => onChange?.(fn({ ...value, weights: { ...weights } }));

  const addMember = (m) => {
    if (guardReason(m)) return;
    mutate((c) => ({ ...c, weights: { ...c.weights, [m.id]: 1 } }));
  };
  const removeMember = (id) => mutate((c) => {
    const w = { ...c.weights };
    delete w[id];
    return { ...c, weights: w, fixed_member_id: c.fixed_member_id === id ? null : c.fixed_member_id };
  });
  const setWeight = (id, val) => mutate((c) => ({ ...c, weights: { ...c.weights, [id]: clampWeight(val) } }));
  const setFixed = (id) => mutate((c) => ({ ...c, fixed_member_id: id }));
  const setMode = (next) => mutate((c) => {
    let fixed = c.fixed_member_id;
    if (next === 'fixed' && (!fixed || !(fixed in c.weights))) {
      fixed = poolMembers[0]?.id || null;
    }
    return { ...c, mode: next, fixed_member_id: next === 'fixed' ? fixed : c.fixed_member_id };
  });
  const seedFromCategory = (catId) => {
    if (!catId) return;
    mutate((c) => {
      const w = { ...c.weights };
      for (const m of members) {
        if (isActive(m) && m.team_category_id === catId && !guardReason(m) && !(m.id in w)) {
          w[m.id] = 1;
        }
      }
      return { ...c, weights: w, target_category_id: catId };
    });
  };

  const sectionLabel = (txt) => (
    <div style={{
      fontSize: 11, fontWeight: 700, color: colors.textTertiary,
      textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{txt}</div>
  );

  const avatar = (
    <span style={{
      width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(245,158,11,0.15)', color: '#b45309',
    }}>
      <User size={13} />
    </span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Modo */}
      {allowFixed && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sectionLabel('Modo')}
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { key: 'round_robin', label: 'Roleta ponderada' },
            { key: 'fixed', label: 'Membro fixo' },
          ].map((opt) => {
            const on = mode === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setMode(opt.key)}
                style={{
                  padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${on ? colors.accentPrimary : colors.borderColor}`,
                  backgroundColor: on ? `${colors.accentPrimary}15` : 'transparent',
                  color: on ? colors.accentPrimary : colors.textSecondary,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Pool */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {sectionLabel('Na roleta')}
          {mode === 'round_robin' && poolMembers.length > 0 && (
            <span style={{ fontSize: 11, color: colors.textTertiary }}>peso · divisão esperada</span>
          )}
        </div>

        {poolMembers.length === 0 ? (
          <span style={{ fontSize: 12.5, color: colors.textTertiary, lineHeight: 1.45 }}>
            Nenhum membro na roleta ainda. Adicione abaixo ou puxe de uma categoria.
          </span>
        ) : poolMembers.map((m) => {
          const guarded = guardReason(m);
          const selectedFixed = value?.fixed_member_id === m.id;
          return (
            <div
              key={m.id}
              style={{
                display: 'flex', flexDirection: 'column', gap: 6,
                padding: '9px 12px', borderRadius: 8,
                backgroundColor: (mode === 'fixed' && selectedFixed) ? 'rgba(245,158,11,0.10)' : colors.bgPrimary,
                border: `1px solid ${(mode === 'fixed' && selectedFixed) ? 'rgba(245,158,11,0.45)' : colors.borderColor}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {mode === 'fixed' && (
                  <input
                    type="radio"
                    name={`fixed_${radioName}`}
                    checked={selectedFixed}
                    onChange={() => setFixed(m.id)}
                    style={{ accentColor: AMBER, cursor: 'pointer', flexShrink: 0 }}
                  />
                )}
                {avatar}
                <span style={{ fontSize: 13, color: colors.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nameOf(m)}
                </span>
                {mode === 'round_robin' && (
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={clampWeight(weights[m.id])}
                    onChange={(e) => setWeight(m.id, e.target.value)}
                    style={{
                      width: 52, backgroundColor: colors.bgSecondary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: 8,
                      padding: '4px 6px', color: colors.textPrimary, fontSize: 12,
                      outline: 'none', fontFamily: 'inherit', textAlign: 'center',
                    }}
                  />
                )}
                <span style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, minWidth: 42, textAlign: 'right' }}>
                  {mode === 'fixed' ? pct(m.id).toFixed(0) : pct(m.id).toFixed(1)}%
                </span>
                <button
                  onClick={() => removeMember(m.id)}
                  title="Remover da roleta"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textTertiary, padding: 2, borderRadius: 6, display: 'flex', flexShrink: 0 }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
              {mode === 'round_robin' && (
                <div style={{ height: 7, borderRadius: 4, backgroundColor: colors.bgSecondary, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct(m.id)}%`, backgroundColor: colors.accentPrimary, borderRadius: 4 }} />
                </div>
              )}
              {guarded && (
                <span style={{ fontSize: 10.5, color: '#d97706' }}>Não recomendado — {guarded}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Adicionar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sectionLabel('Adicionar à roleta')}

        {categories.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: colors.textSecondary, flexShrink: 0 }}>Adicionar todos de:</span>
            <select
              value=""
              onChange={(e) => { seedFromCategory(e.target.value); e.target.value = ''; }}
              style={{
                flex: 1, backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.borderColor}`, borderRadius: 8,
                padding: '6px 8px', color: colors.textPrimary, fontSize: 12,
                outline: 'none', fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <option value="">Escolher categoria…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        )}

        {addableMembers.length === 0 ? (
          <span style={{ fontSize: 12, color: colors.textTertiary }}>
            Todos os membros elegíveis já estão na roleta.
          </span>
        ) : addableMembers.map((m) => {
          const reason = guardReason(m);
          return (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${colors.borderColor}` }}>
              {avatar}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nameOf(m)}
                </div>
                {reason && <div style={{ fontSize: 10.5, color: colors.textTertiary }}>{reason}</div>}
              </div>
              <button
                onClick={() => addMember(m)}
                disabled={!!reason}
                title={reason || 'Adicionar à roleta'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8,
                  border: `1px solid ${reason ? colors.borderColor : colors.accentPrimary}`,
                  backgroundColor: reason ? 'transparent' : `${colors.accentPrimary}12`,
                  color: reason ? colors.textTertiary : colors.accentPrimary,
                  fontSize: 12, fontWeight: 600, cursor: reason ? 'not-allowed' : 'pointer', flexShrink: 0,
                }}
              >
                <Plus size={13} /> Adicionar
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RouletteConfigForm;
